import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Eye, EyeSlash, Lock, LockOpen, Trash,
  CaretRight, CaretDown, LinkBreak, FolderPlus, Folder, FolderOpen, ArrowSquareIn, ArrowSquareOut, CopySimple,
} from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { useAssetStore } from '../../stores/assetStore';
import { getSpineManager } from '../../lib/spine-manager';
import { Tooltip } from '../ui/tooltip';
import { ContextMenu, ContextMenuTrigger } from '../ui/context-menu';
import LayerContextMenu from './LayerContextMenu';
import type { Layer, LayerGroup } from '@spine-workspace/shared';

const BADGE_COLORS = [
  'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-green-500',
  'bg-teal-500', 'bg-blue-500', 'bg-violet-500', 'bg-pink-500',
] as const;

function badgeColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return BADGE_COLORS[h % BADGE_COLORS.length];
}

// ── Label color picker ────────────────────────────────────────────────────────

const LABEL_COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6','#a855f7','#ec4899','#94a3b8'];

function LayerColorPicker({ layerId, currentColor }: { layerId: string; currentColor?: string | null }) {
  const [open, setOpen] = useState(false);
  const { setLayerColor } = useSceneStore();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Tooltip content="Label color">
        <button
          data-testid={`layer-color-btn-${layerId}`}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          className="w-[22px] h-[22px] flex items-center justify-center rounded hover:bg-bg-hover transition-all duration-fast"
          title="Label color"
        >
          <span
            className="w-3 h-3 rounded-full border border-border"
            style={{ backgroundColor: currentColor ?? 'transparent' }}
          />
        </button>
      </Tooltip>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 bg-bg-elevated border border-border rounded-lg p-2 shadow-lg flex flex-wrap gap-1"
          style={{ width: '88px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="layer-color-clear"
            onClick={() => { setLayerColor(layerId, null); setOpen(false); }}
            className="w-5 h-5 rounded-full border border-dashed border-border hover:border-text-tertiary flex items-center justify-center text-text-tertiary hover:text-text-primary text-[9px]"
            title="No color"
          >✕</button>
          {LABEL_COLORS.map((c) => (
            <button
              key={c}
              data-testid={`layer-color-swatch-${c.slice(1)}`}
              onClick={() => { setLayerColor(layerId, c); setOpen(false); }}
              className={`w-5 h-5 rounded-full border-2 transition-all duration-fast ${currentColor === c ? 'border-white scale-110' : 'border-transparent hover:border-white/50'}`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline rename (layer or group) ───────────────────────────────────────────

function InlineRename({ name, onRename, className = '' }: { name: string; onRename: (n: string) => void; className?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setDraft(name);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== name) onRename(trimmed);
    setEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') setEditing(false);
    e.stopPropagation();
  }

  // Allow LayerContextMenu to trigger rename via DOM event
  useEffect(() => {
    function onStartRename(e: Event) {
      e.stopPropagation();
      setDraft(name);
      setEditing(true);
      setTimeout(() => inputRef.current?.select(), 0);
    }
    const el = inputRef.current?.closest('[data-rename-layer]');
    el?.addEventListener('start-rename', onStartRename);
    return () => el?.removeEventListener('start-rename', onStartRename);
  });

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className={`flex-1 min-w-0 bg-bg-input border border-accent rounded px-1 py-0 text-sm font-sans text-text-primary outline-none ${className}`}
      />
    );
  }

  return (
    <span
      onDoubleClick={startEdit}
      title="Double-click to rename"
      className={`flex-1 min-w-0 text-sm font-sans font-medium truncate cursor-text select-none ${className}`}
    >
      {name}
    </span>
  );
}

// ── Layer badge / thumbnail ───────────────────────────────────────────────────

function LayerBadge({ layer, thumbnail, size = 'sm' }: { layer: Layer; thumbnail?: string; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'w-5 h-5' : 'w-8 h-8';
  const textSize = size === 'sm' ? 'text-[9px]' : 'text-xs';
  if (thumbnail) {
    return (
      <img
        src={thumbnail}
        alt={layer.name}
        className={`${dim} rounded object-cover shrink-0 border border-border-subtle`}
        data-testid={`layer-thumbnail-${layer.id}`}
      />
    );
  }
  const bgClass = layer.labelColor ? undefined : badgeColor(layer.id);
  return (
    <div
      data-testid={`layer-badge-${layer.id}`}
      className={`${dim} rounded flex items-center justify-center shrink-0 text-white ${textSize} font-bold ${bgClass ?? ''}`}
      style={layer.labelColor ? { backgroundColor: layer.labelColor } : undefined}
    >
      {layer.name.charAt(0).toUpperCase()}
    </div>
  );
}

// ── Child layer (bone-attached, indented) ─────────────────────────────────────

function ChildLayer({ layer, thumbnail }: { layer: Layer; thumbnail?: string }) {
  const { selectedLayerId, selectLayer, toggleLayerVisibility, removeLayer, setLayerAttachment } =
    useSceneStore();
  const isSelected = layer.id === selectedLayerId;

  return (
    <div
      onClick={(e) => { selectLayer(layer.id, { multi: e.ctrlKey || e.metaKey, range: e.shiftKey }); useAssetStore.getState().selectAsset(null); }}
      className={`relative flex items-center gap-2 mx-3 ml-10 my-0.5 px-2 py-1.5 rounded-md cursor-pointer transition-all duration-normal group ${
        isSelected
          ? 'bg-accent-muted outline outline-[1.5px] outline-accent/30'
          : 'hover:bg-bg-hover'
      }`}
    >
      <LayerBadge layer={layer} thumbnail={thumbnail} size="sm" />
      <div className="flex-1 min-w-0">
        <InlineRename
          name={layer.name}
          onRename={(n) => useSceneStore.getState().renameLayer(layer.id, n)}
        />
        {layer.attachment.boneName && (
          <div className="text-[10px] font-mono text-text-tertiary truncate leading-none mt-0.5">
            @ {layer.attachment.boneName}
          </div>
        )}
      </div>

      {/* Actions — absolute overlay, visible on hover only */}
      <div className="absolute right-0 inset-y-0 flex items-center rounded-r-md transition-opacity duration-fast opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
        <div className={`w-5 h-full shrink-0 bg-gradient-to-r from-transparent ${isSelected ? 'to-accent-muted' : 'to-bg-hover'}`} />
        <div className={`flex items-center gap-0.5 pr-1 h-full ${isSelected ? 'bg-accent-muted' : 'bg-bg-hover'}`}>
          <Tooltip content="Detach from parent">
            <button
              title="Detach from parent"
              onClick={(e) => { e.stopPropagation(); setLayerAttachment(layer.id, null, null); }}
              className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast"
            >
              <LinkBreak size={12} />
            </button>
          </Tooltip>
          <Tooltip content={layer.visible ? 'Hide layer' : 'Show layer'}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleLayerVisibility(layer.id);
                getSpineManager().setLayerVisible(layer.id, !layer.visible);
              }}
              className={`w-[22px] h-[22px] flex items-center justify-center rounded transition-all duration-fast ${
                layer.visible
                  ? 'text-text-tertiary hover:text-text-primary hover:bg-bg-active'
                  : 'text-accent hover:bg-accent-muted'
              }`}
            >
              {layer.visible ? <Eye size={12} /> : <EyeSlash size={12} />}
            </button>
          </Tooltip>
          <Tooltip content="Delete layer">
            <button
              onClick={(e) => { e.stopPropagation(); removeLayer(layer.id); }}
              className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast"
            >
              <Trash size={12} />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

// ── Sortable layer row ─────────────────────────────────────────────────────────

interface SortableLayerProps {
  layer: Layer;
  childLayers: Layer[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  thumbnail?: string;
  childThumbnails?: Record<string, string>;
  groups: LayerGroup[];
  indent?: boolean;
}

function SortableLayer({ layer, childLayers, collapsed, onToggleCollapse, childThumbnails = {}, groups, indent = false }: SortableLayerProps) {
  const { selectedLayerId, selectedLayerIds, selectLayer, toggleLayerVisibility, soloLayer, soloedLayerId, toggleLayerLock, removeLayer, setLayerGroup, duplicateLayer } =
    useSceneStore();
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [groupMenuPos, setGroupMenuPos] = useState({ top: 0, left: 0 });
  const groupBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!groupMenuOpen) return;
    function onOutside(e: MouseEvent) {
      if (groupBtnRef.current && !groupBtnRef.current.closest('[data-group-menu-root]')?.contains(e.target as Node)) {
        setGroupMenuOpen(false);
      }
    }
    function onScroll() { setGroupMenuOpen(false); }
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [groupMenuOpen]);

  function openGroupMenu(e: React.MouseEvent) {
    e.stopPropagation();
    const rect = groupBtnRef.current?.getBoundingClientRect();
    if (rect) {
      setGroupMenuPos({ top: rect.bottom + 4, left: rect.right - 128 });
    }
    setGroupMenuOpen((o) => !o);
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: layer.id,
  });

  const isSelected = selectedLayerIds.includes(layer.id) || layer.id === selectedLayerId;
  const hasChildren = childLayers.length > 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const inGroup = !!layer.groupId;
  const indentClass = indent ? 'ml-5' : '';

  return (
    <div ref={setNodeRef} style={style} className={indentClass}>
      <ContextMenu>
        <ContextMenuTrigger asChild>
      <div
        {...listeners}
        {...attributes}
        data-testid={`layer-row-${layer.id}`}
        data-rename-layer={layer.id}
        onClick={(e) => {
          selectLayer(layer.id, { multi: e.ctrlKey || e.metaKey, range: e.shiftKey });
          useAssetStore.getState().selectAsset(null);
        }}
        onDoubleClick={() => {
          selectLayer(layer.id);
          useAssetStore.getState().selectAsset(null);
          getSpineManager().focusLayer(layer.id);
        }}
        className={`relative flex items-center gap-1 mx-3 my-0.5 px-2 py-2 rounded-md cursor-grab active:cursor-grabbing transition-all duration-normal group ${
          isSelected
            ? 'bg-accent-muted outline outline-[1.5px] outline-accent/30'
            : 'hover:bg-bg-hover'
        }`}
      >

        {/* Inline visibility toggle (always visible, PTS-style) */}
        <Tooltip content={layer.visible ? 'Hide layer (Alt = solo)' : 'Show layer'}>
          <button
            data-testid={`layer-visibility-${layer.id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (e.altKey) {
                soloLayer(layer.id);
                const mgr = getSpineManager();
                useSceneStore.getState().layers.forEach((l) => mgr.setLayerVisible(l.id, l.visible));
              } else {
                toggleLayerVisibility(layer.id);
                getSpineManager().setLayerVisible(layer.id, !layer.visible);
              }
            }}
            className={`w-[18px] h-[18px] flex items-center justify-center rounded shrink-0 transition-all duration-fast ${
              soloedLayerId === layer.id
                ? 'text-amber-400'
                : layer.visible
                  ? 'text-text-tertiary/40 hover:text-text-secondary'
                  : 'text-error/80 hover:text-error'
            }`}
          >
            {layer.visible ? <Eye size={12} /> : <EyeSlash size={12} />}
          </button>
        </Tooltip>

        {/* Inline lock toggle (always visible, PTS-style) */}
        <Tooltip content={layer.locked ? 'Unlock layer' : 'Lock layer'}>
          <button
            onClick={(e) => { e.stopPropagation(); toggleLayerLock(layer.id); }}
            className={`w-[18px] h-[18px] flex items-center justify-center rounded shrink-0 transition-all duration-fast ${
              layer.locked
                ? 'text-accent hover:text-accent/70'
                : 'text-text-tertiary/30 hover:text-text-secondary'
            }`}
          >
            {layer.locked ? <Lock size={12} /> : <LockOpen size={12} />}
          </button>
        </Tooltip>

        {/* Collapse chevron (for bone-children) */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            className="shrink-0 text-text-tertiary hover:text-text-primary transition-colors duration-fast"
          >
            {collapsed ? <CaretRight size={12} /> : <CaretDown size={12} />}
          </button>
        ) : (
          <div className="w-3 shrink-0" />
        )}

        {/* Name */}
        <InlineRename
          name={layer.name}
          onRename={(n) => useSceneStore.getState().renameLayer(layer.id, n)}
          className={isSelected ? 'text-text-primary' : 'text-text-secondary'}
        />

        {/* Actions — absolute overlay, visible on hover only (selected bg already signals selection) */}
        <div className="absolute right-0 inset-y-0 flex items-center rounded-r-md transition-opacity duration-fast opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
          {/* Gradient fade from row bg into solid button area */}
          <div className={`w-6 h-full shrink-0 bg-gradient-to-r from-transparent ${isSelected ? 'to-accent-muted' : 'to-bg-hover'}`} />
          <div className={`flex items-center gap-0.5 pr-1 h-full ${isSelected ? 'bg-accent-muted' : 'bg-bg-hover'}`}>

            {/* Move to group / ungroup */}
            {inGroup ? (
              <Tooltip content="Remove from group">
                <button
                  onClick={(e) => { e.stopPropagation(); setLayerGroup(layer.id, null); }}
                  data-testid={`layer-ungroup-${layer.id}`}
                  className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast"
                >
                  <ArrowSquareOut size={14} />
                </button>
              </Tooltip>
            ) : groups.length > 0 ? (
              <div data-group-menu-root className="relative">
                <Tooltip content="Add to group">
                  <button
                    ref={groupBtnRef}
                    onClick={openGroupMenu}
                    data-testid={`layer-addtogroup-${layer.id}`}
                    className={`w-[22px] h-[22px] flex items-center justify-center rounded transition-all duration-fast ${
                      groupMenuOpen ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-accent hover:bg-accent-muted'
                    }`}
                  >
                    <ArrowSquareIn size={14} />
                  </button>
                </Tooltip>
                {groupMenuOpen && typeof document !== 'undefined' && ReactDOM.createPortal(
                  <div
                    style={{ position: 'fixed', top: groupMenuPos.top, left: groupMenuPos.left, zIndex: 9999 }}
                    className="bg-bg-elevated border border-border rounded-lg shadow-lg py-1 min-w-[128px]"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {groups.map((g) => (
                      <button
                        key={g.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLayerGroup(layer.id, g.id);
                          setGroupMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors text-left"
                      >
                        <Folder size={11} className="text-accent/70 shrink-0" />
                        <span className="truncate">{g.name}</span>
                      </button>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
            ) : null}

            <Tooltip content="Duplicate layer (Ctrl+D)">
              <button
                data-testid={`layer-duplicate-${layer.id}`}
                onClick={(e) => { e.stopPropagation(); duplicateLayer(layer.id); }}
                className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast"
              >
                <CopySimple size={14} />
              </button>
            </Tooltip>

            <LayerColorPicker layerId={layer.id} currentColor={layer.labelColor} />

            <Tooltip content="Delete layer">
              <button
                onClick={(e) => { e.stopPropagation(); removeLayer(layer.id); }}
                className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast"
              >
                <Trash size={14} />
              </button>
            </Tooltip>

          </div>
        </div>
      </div>
        </ContextMenuTrigger>
        <LayerContextMenu layer={layer} groups={groups} />
      </ContextMenu>

      {/* Bone-attached children */}
      {hasChildren && !collapsed && childLayers.map((child) => (
        <ChildLayer key={child.id} layer={child} thumbnail={childThumbnails[child.id]} />
      ))}
    </div>
  );
}

// ── Sortable group header row ──────────────────────────────────────────────────

function GroupHeader({ group, isDropTarget = false }: { group: LayerGroup; isDropTarget?: boolean }) {
  const { toggleGroupCollapsed, removeLayerGroup, renameLayerGroup, selectGroup, selectGroupLayers, selectedGroupId, selectLayers, selectedLayerIds, layers } = useSceneStore();
  const isSelected = selectedGroupId === group.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `group:${group.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  function handleGroupClick(e: React.MouseEvent) {
    if (e.shiftKey) {
      // Shift+click group: add all group's layers to current selection
      const groupLayerIds = layers.filter((l) => l.groupId === group.id).map((l) => l.id);
      const merged = [...new Set([...selectedLayerIds, ...groupLayerIds])];
      selectLayers(merged);
    } else {
      selectGroup(isSelected ? null : group.id);
    }
  }

  return (
    <div ref={setNodeRef} style={style}>
    <div
      {...listeners}
      {...attributes}
      data-testid={`layer-group-${group.id}`}
      data-group-drop-id={group.id}
      onClick={handleGroupClick}
      className={`relative flex items-center gap-1.5 mx-2 my-1 px-2 py-1 rounded-md border transition-colors duration-fast group cursor-grab active:cursor-grabbing ${
        isDropTarget
          ? 'bg-accent-muted border-accent/40 text-accent'
          : isSelected
            ? 'bg-accent-muted border-accent/30'
            : 'bg-bg-input border-border-subtle hover:bg-bg-hover'
      }`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); toggleGroupCollapsed(group.id); }}
        data-testid={`layer-group-collapse-${group.id}`}
        className="shrink-0 text-text-tertiary hover:text-text-primary transition-colors duration-fast"
      >
        {group.collapsed
          ? <Folder size={13} className="text-accent/70" />
          : <FolderOpen size={13} className="text-accent/70" />
        }
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); toggleGroupCollapsed(group.id); }}
        className="shrink-0 text-text-tertiary hover:text-text-primary"
      >
        {group.collapsed ? <CaretRight size={11} /> : <CaretDown size={11} />}
      </button>

      <InlineRename
        name={group.name}
        onRename={(n) => renameLayerGroup(group.id, n)}
        className={`text-xs font-mono ${isSelected ? 'text-accent' : 'text-text-secondary'}`}
      />

      {/* Hover action buttons */}
      <div className="absolute right-0 inset-y-0 flex items-center rounded-r-md transition-opacity duration-fast opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
        <div className={`w-4 h-full shrink-0 bg-gradient-to-r from-transparent ${isSelected ? 'to-accent-muted' : 'to-bg-hover'}`} />
        <div className={`flex items-center gap-0.5 pr-1 h-full ${isSelected ? 'bg-accent-muted' : 'bg-bg-hover'}`}>
          <Tooltip content="Select all layers in group">
            <button
              data-testid={`layer-group-select-all-${group.id}`}
              onClick={(e) => { e.stopPropagation(); selectGroupLayers(group.id); }}
              className="px-1.5 h-[22px] flex items-center justify-center rounded text-[9px] font-mono text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast"
            >
              All
            </button>
          </Tooltip>
          <Tooltip content="Deselect all">
            <button
              data-testid={`layer-group-deselect-all-${group.id}`}
              onClick={(e) => { e.stopPropagation(); useSceneStore.getState().selectLayer(null); }}
              className="px-1.5 h-[22px] flex items-center justify-center rounded text-[9px] font-mono text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
            >
              None
            </button>
          </Tooltip>
          <button
            onClick={(e) => { e.stopPropagation(); removeLayerGroup(group.id); }}
            data-testid={`layer-group-delete-${group.id}`}
            className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast shrink-0"
          >
            <Trash size={11} />
          </button>
        </div>
      </div>
    </div>
    </div>
  );
}

// ── Main LayerList ─────────────────────────────────────────────────────────────

export default function LayerList({ filter = '' }: { filter?: string }) {
  const { layers, layerGroups, reorderLayers, reorderGroups, addLayerGroup, selectedLayerId, selectLayer, selectLayers, selectedGroupId, selectGroup, toggleGroupCollapsed } = useSceneStore();
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [dropTargetGroupId, setDropTargetGroupId] = useState<string | null>(null);
  const captureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Anchor for Shift+Arrow range selection (layer ID, reset on plain navigation)
  const shiftAnchorRef = useRef<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /** Find which group header (if any) is under the given client coordinates */
  function getGroupIdAtPoint(x: number, y: number): string | null {
    const headers = document.querySelectorAll<HTMLElement>('[data-group-drop-id]');
    for (const el of headers) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        return el.dataset.groupDropId ?? null;
      }
    }
    return null;
  }

  // Capture thumbnails after a short delay (lets PixiJS render first)
  const scheduleThumbnailCapture = useCallback(() => {
    if (captureTimerRef.current) clearTimeout(captureTimerRef.current);
    captureTimerRef.current = setTimeout(() => {
      const mgr = getSpineManager();
      const newThumbs: Record<string, string> = {};
      for (const layer of useSceneStore.getState().layers) {
        const thumb = mgr.captureLayerThumbnail(layer.id);
        if (thumb) newThumbs[layer.id] = thumb;
      }
      if (Object.keys(newThumbs).length > 0) {
        setThumbnails((prev) => ({ ...prev, ...newThumbs }));
      }
    }, 800);
  }, []);

  useEffect(() => {
    scheduleThumbnailCapture();
    return () => { if (captureTimerRef.current) clearTimeout(captureTimerRef.current); };
  }, [layers.length, scheduleThumbnailCapture]);

  // Build child map (bone-attached children)
  const childMap = new Map<string, Layer[]>();
  const rootLayers: Layer[] = [];
  const filterLower = filter.toLowerCase();
  for (const layer of layers) {
    const pid = layer.attachment.parentLayerId;
    if (pid) {
      const arr = childMap.get(pid) ?? [];
      arr.push(layer);
      childMap.set(pid, arr);
    } else {
      if (!filterLower || layer.name.toLowerCase().includes(filterLower)) {
        rootLayers.push(layer);
      }
    }
  }

  function toggleCollapse(id: string) {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleDragMove(event: DragOverEvent) {
    const ptr = event.activatorEvent as PointerEvent;
    const x = ptr.clientX + event.delta.x;
    const y = ptr.clientY + event.delta.y;
    setDropTargetGroupId(getGroupIdAtPoint(x, y));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setDropTargetGroupId(null);

    // Check if dropped onto a group header (via pointer position)
    const ptr = event.activatorEvent as PointerEvent;
    const dropX = ptr.clientX + event.delta.x;
    const dropY = ptr.clientY + event.delta.y;
    const targetGroupId = getGroupIdAtPoint(dropX, dropY);

    if (targetGroupId) {
      const layerId = String(active.id);
      if (layers.find((l) => l.id === layerId)) {
        useSceneStore.getState().setLayerGroup(layerId, targetGroupId);
      }
      return;
    }

    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Group reorder: both are group: prefixed IDs
    if (activeId.startsWith('group:') && overId.startsWith('group:')) {
      reorderGroups(activeId.slice(6), overId.slice(6));
      return;
    }

    // Layer reorder
    const oldIndex = layers.findIndex((l) => l.id === activeId);
    const newIndex = layers.findIndex((l) => l.id === overId);
    if (oldIndex !== -1 && newIndex !== -1) {
      reorderLayers(oldIndex, newIndex);
      getSpineManager().reorderLayers(useSceneStore.getState().layers.map((l) => l.id));
    }
  }

  if (layers.length === 0 && layerGroups.length === 0) {
    return (
      <div>
        <div className="px-3 py-1.5 border-b border-border-subtle flex justify-end">
          <Tooltip content="New layer group">
            <button
              onClick={() => addLayerGroup()}
              data-testid="add-layer-group"
              className="flex items-center gap-1 px-2 py-1 rounded text-text-tertiary hover:text-accent hover:bg-accent-muted text-xs font-mono transition-all duration-fast"
            >
              <FolderPlus size={13} />
              <span>Group</span>
            </button>
          </Tooltip>
        </div>
        <p className="text-text-tertiary text-xs px-4 py-4 font-sans">
          Drag assets onto the canvas to add layers
        </p>
      </div>
    );
  }

  const reversedRoots = [...rootLayers].reverse();

  // Sort groups by sortOrder
  const sortedGroups = [...layerGroups].sort((a, b) => a.sortOrder - b.sortOrder);

  // Partition root layers: ungrouped vs per-group
  const ungroupedLayers = reversedRoots.filter((l) => !l.groupId);
  const groupedLayersMap = new Map<string, Layer[]>();
  for (const layer of reversedRoots) {
    if (layer.groupId) {
      const arr = groupedLayersMap.get(layer.groupId) ?? [];
      arr.push(layer);
      groupedLayersMap.set(layer.groupId, arr);
    }
  }

  function handleDragStart() {
    // Clear multi-selection when dragging — only the dragged item moves
    useSceneStore.getState().clearMultiSelection();
  }

  // Build flat ordered list of navigable items (groups + layers, matching visual order)
  type NavItem = { type: 'group'; id: string } | { type: 'layer'; id: string };
  const flatNav: NavItem[] = [];
  for (const group of sortedGroups) {
    flatNav.push({ type: 'group', id: group.id });
    if (!group.collapsed) {
      for (const layer of (groupedLayersMap.get(group.id) ?? [])) {
        flatNav.push({ type: 'layer', id: layer.id });
      }
    }
  }
  for (const layer of ungroupedLayers) {
    flatNav.push({ type: 'layer', id: layer.id });
  }

  function handleListKeyDown(e: React.KeyboardEvent) {
    const isVertical = e.key === 'ArrowUp' || e.key === 'ArrowDown';
    const isHorizontal = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
    if (!isVertical && !isHorizontal) return;
    if (flatNav.length === 0) return;
    e.preventDefault();

    // Find current position in flatNav
    const currentIdx = flatNav.findIndex((item) =>
      item.type === 'group' ? item.id === selectedGroupId : item.id === selectedLayerId
    );

    if (isHorizontal) {
      // ←/→ expand/collapse the focused group (Shift doesn't change this)
      const focusedGroup = selectedGroupId
        ? layerGroups.find((g) => g.id === selectedGroupId)
        : null;
      if (!focusedGroup) return;
      const shouldCollapse = e.key === 'ArrowLeft' && !focusedGroup.collapsed;
      const shouldExpand = e.key === 'ArrowRight' && focusedGroup.collapsed;
      if (shouldCollapse || shouldExpand) toggleGroupCollapsed(focusedGroup.id);
      return;
    }

    // ↑/↓ navigate
    const nextIdx = e.key === 'ArrowDown'
      ? Math.min(currentIdx < 0 ? 0 : currentIdx + 1, flatNav.length - 1)
      : Math.max(currentIdx < 0 ? 0 : currentIdx - 1, 0);
    const next = flatNav[nextIdx];
    if (!next) return;

    if (e.shiftKey && (next.type === 'layer' || next.type === 'group')) {
      // Shift+Arrow: extend selection from anchor to cursor
      if (!shiftAnchorRef.current) {
        shiftAnchorRef.current = selectedLayerId ?? (next.type === 'layer' ? next.id : null);
      }
      const anchorId = shiftAnchorRef.current;
      const anchorIdx = anchorId
        ? flatNav.findIndex((item) => item.type === 'layer' && item.id === anchorId)
        : -1;
      const [lo, hi] = anchorIdx >= 0 && anchorIdx <= nextIdx ? [anchorIdx, nextIdx] : anchorIdx >= 0 ? [nextIdx, anchorIdx] : [nextIdx, nextIdx];
      // Include layers directly + layers of any groups in range
      const rangeIds: string[] = [];
      for (const item of flatNav.slice(lo, hi + 1)) {
        if (item.type === 'layer') {
          rangeIds.push(item.id);
        } else {
          // Group in range: add all its layers
          layers.filter((l) => l.groupId === item.id).forEach((l) => rangeIds.push(l.id));
        }
      }
      const uniqueIds = [...new Set(rangeIds)];
      if (uniqueIds.length === 0 && next.type === 'group') {
        // Only a group selected — select its layers
        const groupLayerIds = layers.filter((l) => l.groupId === next.id).map((l) => l.id);
        if (groupLayerIds.length) selectLayers(groupLayerIds);
        else selectGroup(next.id);
      } else {
        selectLayers(uniqueIds.length > 0 ? uniqueIds : [selectedLayerId ?? ''].filter(Boolean));
      }
      useAssetStore.getState().selectAsset(null);
      setTimeout(() => {
        listRef.current?.querySelector(`[data-testid="layer-row-${next.id}"]`)?.scrollIntoView({ block: 'nearest' });
      }, 0);
    } else if (next.type === 'group') {
      shiftAnchorRef.current = null;
      selectGroup(next.id);
      useAssetStore.getState().selectAsset(null);
      setTimeout(() => {
        listRef.current?.querySelector(`[data-testid="layer-group-${next.id}"]`)?.scrollIntoView({ block: 'nearest' });
      }, 0);
    } else {
      // Plain navigation — reset anchor
      shiftAnchorRef.current = null;
      selectLayer(next.id);
      useAssetStore.getState().selectAsset(null);
      setTimeout(() => {
        listRef.current?.querySelector(`[data-testid="layer-row-${next.id}"]`)?.scrollIntoView({ block: 'nearest' });
      }, 0);
    }
  }

  const groupSortIds = sortedGroups.map((g) => `group:${g.id}`);
  const layerSortIds = reversedRoots.map((l) => l.id);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
      <SortableContext items={[...groupSortIds, ...layerSortIds]} strategy={verticalListSortingStrategy}>
        <div ref={listRef} data-nav-panel="hierarchy" tabIndex={0} onKeyDown={handleListKeyDown} className="py-1 overflow-y-auto outline-none">
          {/* New group button */}
          <div className="px-3 py-1 flex justify-end">
            <Tooltip content="New layer group">
              <button
                onClick={() => addLayerGroup()}
                data-testid="add-layer-group"
                className="flex items-center gap-1 px-2 py-0.5 rounded text-text-tertiary hover:text-accent hover:bg-accent-muted text-xs font-mono transition-all duration-fast"
              >
                <FolderPlus size={12} />
                <span>Group</span>
              </button>
            </Tooltip>
          </div>

          {/* Groups (with their layers inside) */}
          {sortedGroups.map((group) => {
            const groupLayers = groupedLayersMap.get(group.id) ?? [];
            return (
              <div key={group.id}>
                <GroupHeader group={group} isDropTarget={dropTargetGroupId === group.id} />
                {!group.collapsed && groupLayers.map((layer) => {
                  const children = childMap.get(layer.id) ?? [];
                  const childThumbs: Record<string, string> = {};
                  for (const c of children) {
                    if (thumbnails[c.id]) childThumbs[c.id] = thumbnails[c.id];
                  }
                  return (
                    <SortableLayer
                      key={layer.id}
                      layer={layer}
                      childLayers={children}
                      collapsed={collapsedIds.has(layer.id)}
                      onToggleCollapse={() => toggleCollapse(layer.id)}
                      thumbnail={thumbnails[layer.id]}
                      childThumbnails={childThumbs}
                      groups={layerGroups}
                      indent
                    />
                  );
                })}
              </div>
            );
          })}

          {/* Ungrouped layers */}
          {ungroupedLayers.map((layer) => {
            const children = childMap.get(layer.id) ?? [];
            const childThumbs: Record<string, string> = {};
            for (const c of children) {
              if (thumbnails[c.id]) childThumbs[c.id] = thumbnails[c.id];
            }
            return (
              <SortableLayer
                key={layer.id}
                layer={layer}
                childLayers={children}
                collapsed={collapsedIds.has(layer.id)}
                onToggleCollapse={() => toggleCollapse(layer.id)}
                thumbnail={thumbnails[layer.id]}
                childThumbnails={childThumbs}
                groups={layerGroups}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}
